/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useMemo, useState } from 'react';
import InputMask from 'react-input-mask';
import Select from 'react-select';
import { cleanCnpj, validateCnpj } from 'utils/cnpj';
import { isPublicIpv4, isPublicIpv6, isValidIpv4, isValidIpv6, normalizeIp } from 'utils/ip';
import {
  CNPJ_MASK,
  MAX_LIST_ROWS,
  emptyIpRow,
  inputClass,
  nextRowId,
  selectStyles,
} from '../helpers';
import { HOSTING_OPTIONS, IP_VERSION_OPTIONS } from '../options';
import { IpRow, IpServersValues, IpVersion } from '../types';

type RowField = Exclude<keyof IpRow, 'id'>;
type RowErrors = Partial<Record<RowField, string>>;

interface IpServersErrors {
  ips: Record<number, RowErrors>;
  servidores_localizacao?: string;
}

interface IpServersStepProps {
  values: IpServersValues;
  onChange: (values: IpServersValues) => void;
  onNext: () => void;
  onBack: () => void;
}

const validate = (values: IpServersValues): IpServersErrors => {
  const errors: IpServersErrors = { ips: {} };
  const seen: string[] = [];

  values.ips.forEach((row) => {
    const rowErrors: RowErrors = {};
    const ip = row.ip.trim();
    const isValidFormat = row.ip_versao === 'ipv6' ? isValidIpv6(ip) : isValidIpv4(ip);
    const isPublic = row.ip_versao === 'ipv6' ? isPublicIpv6(ip) : isPublicIpv4(ip);

    if (!ip) {
      rowErrors.ip = 'Informe o IP.';
    } else if (!isValidFormat) {
      rowErrors.ip =
        row.ip_versao === 'ipv6'
          ? 'IPv6 inválido. Ex.: 2804:14d:5c80::100.'
          : 'IPv4 inválido. Use o formato 200.100.50.25.';
    } else if (!isPublic) {
      rowErrors.ip = 'Informe um IP público. Endereços internos não funcionam.';
    } else if (seen.includes(normalizeIp(ip))) {
      rowErrors.ip = 'IP repetido.';
    }

    seen.push(normalizeIp(ip));

    if (!row.tipo_hospedagem) {
      rowErrors.tipo_hospedagem = 'Selecione onde fica o servidor.';
    }

    if (row.tipo_hospedagem === 'operadora') {
      if (!cleanCnpj(row.ip_titular_cnpj)) {
        rowErrors.ip_titular_cnpj = 'Informe o CNPJ titular do IP.';
      } else if (!validateCnpj(row.ip_titular_cnpj)) {
        rowErrors.ip_titular_cnpj = 'Informe um CNPJ válido.';
      }
    }

    if (row.tipo_hospedagem === 'nuvem' && row.provedor_nuvem.trim().length < 2) {
      rowErrors.provedor_nuvem = 'Informe o provedor de nuvem.';
    }

    if (row.tipo_hospedagem && row.ip_localizacao.trim().length < 2) {
      rowErrors.ip_localizacao =
        row.tipo_hospedagem === 'operadora' ? 'Informe a cidade.' : 'Informe a região.';
    }

    if (Object.keys(rowErrors).length > 0) {
      errors.ips[row.id] = rowErrors;
    }
  });

  if (values.servidores_localizacao.trim().length < 2) {
    errors.servidores_localizacao = 'Informe onde ficam os servidores.';
  }

  return errors;
};

const hasErrors = (errors: IpServersErrors) =>
  Object.keys(errors.ips).length > 0 || Boolean(errors.servidores_localizacao);

export const isIpServersComplete = (values: IpServersValues) => !hasErrors(validate(values));

const IpServersStep: React.FC<IpServersStepProps> = ({ values, onChange, onNext, onBack }) => {
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState<boolean>(false);

  const errors = useMemo(() => validate(values), [values]);
  const isValid = !hasErrors(errors);

  const touch = (key: string) => setTouched((prev) => ({ ...prev, [key]: true }));

  const rowError = (id: number, field: RowField) =>
    submitted || touched[`ips.${id}.${field}`] ? errors.ips[id]?.[field] : undefined;

  const updateRow = <K extends RowField>(id: number, field: K, value: IpRow[K]) =>
    onChange({
      ...values,
      ips: values.ips.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    });

  const addIp = () =>
    onChange({ ...values, ips: [...values.ips, emptyIpRow(nextRowId(values.ips))] });

  const removeIp = (id: number) => {
    setTouched((prev) => {
      const next = { ...prev };
      Object.keys(next)
        .filter((key) => key.startsWith(`ips.${id}.`))
        .forEach((key) => {
          delete next[key];
        });
      return next;
    });

    onChange({ ...values, ips: values.ips.filter((row) => row.id !== id) });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);

    if (isValid) onNext();
  };

  const serversError =
    submitted || touched.servidores_localizacao ? errors.servidores_localizacao : undefined;

  return (
    <form noValidate onSubmit={handleSubmit}>
      <p className="card-text font-weight-bold mt-4 mb-1">IPs fixos</p>
      <p className="text-muted small mb-3">
        Cadastre até {MAX_LIST_ROWS} IPs públicos e fixos que vão acessar a API.
      </p>

      {values.ips.map((row, index) => {
        const prefix = `api_request_ip_${row.id}`;
        const ipError = rowError(row.id, 'ip');
        const hostingError = rowError(row.id, 'tipo_hospedagem');
        const holderError = rowError(row.id, 'ip_titular_cnpj');
        const providerError = rowError(row.id, 'provedor_nuvem');
        const locationError = rowError(row.id, 'ip_localizacao');

        return (
          <div key={row.id} className="border rounded p-3 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="font-weight-bold">IP {index + 1}</span>
              {values.ips.length > 1 && (
                <button
                  type="button"
                  className="btn btn-link text-secondary p-0"
                  aria-label={`Remover IP ${index + 1}`}
                  onClick={() => removeIp(row.id)}
                >
                  <span className="material-icons" aria-hidden="true">
                    close
                  </span>
                </button>
              )}
            </div>

            <div className="row">
              <div className="col-md-4 mb-3">
                <label htmlFor={`${prefix}_versao`}>Tipo de IP</label>
                <Select
                  inputId={`${prefix}_versao`}
                  options={IP_VERSION_OPTIONS}
                  value={IP_VERSION_OPTIONS.find((option) => option.value === row.ip_versao)}
                  onChange={(option) =>
                    updateRow(row.id, 'ip_versao', (option ? option.value : 'ipv4') as IpVersion)
                  }
                  isSearchable={false}
                  styles={selectStyles(false)}
                />
              </div>
              <div className="col-md-8 mb-3">
                <label htmlFor={`${prefix}_endereco`}>Endereço IP</label>
                <input
                  id={`${prefix}_endereco`}
                  type="text"
                  inputMode={row.ip_versao === 'ipv6' ? 'text' : 'decimal'}
                  placeholder={
                    row.ip_versao === 'ipv6' ? 'Ex.: 2804:14d:5c80::100' : 'Ex.: 200.100.50.25'
                  }
                  className={inputClass(ipError)}
                  value={row.ip}
                  onChange={(e) => updateRow(row.id, 'ip', e.target.value.replace(/\s/g, ''))}
                  onBlur={() => touch(`ips.${row.id}.ip`)}
                />
                {ipError && <div className="invalid-feedback">{ipError}</div>}
              </div>
            </div>

            <fieldset className="mb-3">
              <legend className="col-form-label pt-0">Onde fica o servidor com esse IP?</legend>
              {HOSTING_OPTIONS.map((option) => {
                const optionId = `${prefix}_${option.value}`;

                return (
                  <div key={option.value} className="form-check form-check-inline">
                    <input
                      id={optionId}
                      type="radio"
                      name={`${prefix}_hospedagem`}
                      className={`form-check-input ${hostingError ? 'is-invalid' : ''}`}
                      checked={row.tipo_hospedagem === option.value}
                      onChange={() => updateRow(row.id, 'tipo_hospedagem', option.value)}
                    />
                    <label className="form-check-label" htmlFor={optionId}>
                      {option.label}
                    </label>
                  </div>
                );
              })}
              {hostingError && <div className="invalid-feedback d-block">{hostingError}</div>}
            </fieldset>

            {row.tipo_hospedagem === 'operadora' && (
              <div className="row">
                <div className="col-md-6 mb-3 mb-md-0">
                  <label htmlFor={`${prefix}_titular`}>CNPJ titular do IP na operadora</label>
                  <InputMask
                    id={`${prefix}_titular`}
                    type="text"
                    mask={CNPJ_MASK}
                    className={inputClass(holderError)}
                    value={row.ip_titular_cnpj}
                    onChange={(e) =>
                      updateRow(row.id, 'ip_titular_cnpj', e.target.value.toUpperCase())
                    }
                    onBlur={() => touch(`ips.${row.id}.ip_titular_cnpj`)}
                  />
                  {holderError && <div className="invalid-feedback">{holderError}</div>}
                </div>
                <div className="col-md-6">
                  <label htmlFor={`${prefix}_cidade`}>Cidade onde o IP está instalado</label>
                  <input
                    id={`${prefix}_cidade`}
                    type="text"
                    placeholder="Ex.: Campinas/SP"
                    className={inputClass(locationError)}
                    value={row.ip_localizacao}
                    onChange={(e) => updateRow(row.id, 'ip_localizacao', e.target.value)}
                    onBlur={() => touch(`ips.${row.id}.ip_localizacao`)}
                  />
                  {locationError && <div className="invalid-feedback">{locationError}</div>}
                </div>
              </div>
            )}

            {row.tipo_hospedagem === 'nuvem' && (
              <div className="row">
                <div className="col-md-6 mb-3 mb-md-0">
                  <label htmlFor={`${prefix}_provedor`}>Provedor de nuvem</label>
                  <input
                    id={`${prefix}_provedor`}
                    type="text"
                    placeholder="Ex.: AWS, Azure, Google Cloud"
                    className={inputClass(providerError)}
                    value={row.provedor_nuvem}
                    onChange={(e) => updateRow(row.id, 'provedor_nuvem', e.target.value)}
                    onBlur={() => touch(`ips.${row.id}.provedor_nuvem`)}
                  />
                  {providerError && <div className="invalid-feedback">{providerError}</div>}
                </div>
                <div className="col-md-6">
                  <label htmlFor={`${prefix}_regiao`}>Região do servidor</label>
                  <input
                    id={`${prefix}_regiao`}
                    type="text"
                    placeholder="Ex.: Central US"
                    className={inputClass(locationError)}
                    value={row.ip_localizacao}
                    onChange={(e) => updateRow(row.id, 'ip_localizacao', e.target.value)}
                    onBlur={() => touch(`ips.${row.id}.ip_localizacao`)}
                  />
                  {locationError && <div className="invalid-feedback">{locationError}</div>}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {values.ips.length < MAX_LIST_ROWS && (
        <div className="text-right">
          <button type="button" className="btn btn-link" onClick={addIp}>
            + adicionar IP
          </button>
        </div>
      )}

      <p className="card-text font-weight-bold mt-4">Servidores</p>
      <div className="mb-4">
        <label htmlFor="api_request_servidores_localizacao">
          Em que cidade ou região ficam os servidores que vão buscar as licitações?
        </label>
        <input
          id="api_request_servidores_localizacao"
          type="text"
          placeholder="Ex.: São Paulo/SP ou Azure Central US"
          className={inputClass(serversError)}
          value={values.servidores_localizacao}
          onChange={(e) => onChange({ ...values, servidores_localizacao: e.target.value })}
          onBlur={() => touch('servidores_localizacao')}
        />
        {serversError && <div className="invalid-feedback">{serversError}</div>}
      </div>

      {submitted && !isValid && (
        <div className="alert alert-danger text-center py-2" role="alert">
          Revise os campos destacados para continuar.
        </div>
      )}

      <div className="row mt-2">
        <div className="col-md-12">
          <button type="submit" className="btn btn-lg btn-block btn-submit">
            Continuar
          </button>
          <div className="text-center mt-2">
            <button type="button" className="btn btn-link" onClick={onBack}>
              Voltar
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default IpServersStep;
